import "./index.less";
import { BaseForm } from "../../../BaseForm";
import { ISelectedObjectiveData, ToastProvider } from "@atman/business";
import { action, observable } from "mobx";
import { t } from "@lingui/macro";

export type DevelopmentPlanType = "leveraging" | "improvement";

export interface IBaseDevelopmentPlanContentProps {
    selectObjectives: (selectedObjectives: Map<string, Map<string, ISelectedObjectiveData>>) => Promise<void>;
    fromExternalApp?: boolean;
}

export abstract class BaseDevelopmentPlanContent<TProps = {}> extends BaseForm<
    IBaseDevelopmentPlanContentProps & TProps,
    {}
> {
    protected get baseClassName() {
        return "BaseDevelopmentPlanContent";
    }

    @observable protected selectedElements: Map<string, Map<string, ISelectedObjectiveData>> = new Map<
        string,
        Map<string, ISelectedObjectiveData>
    >();

    @observable protected erroredElements: Map<string, string[]> = new Map<string, string[]>();

    @action setSelectedElements = (selectedElements: Map<string, Map<string, ISelectedObjectiveData>>): void => {
        this.selectedElements = selectedElements;
    };

    saveAssessment = async (): Promise<void> => {
        const { selectObjectives, fromExternalApp = true } = this.props;

        this.erroredElements.clear();

        if (this.selectedElements.size === 0) {
            // No Competency Selected
            ToastProvider.error(
                t({
                    id: "competencyDevelopment.assessments.global.errors.noSelectedObjective",
                    message: "You need to select at least 1 objective",
                }),
            );
            return;
        }

        for (const [competencyId, selectedObjectives] of [...this.selectedElements]) {
            if (selectedObjectives.size === 0) {
                this.erroredElements.set(competencyId, []);
                return;
            }

            for (const [objectiveId, selectedObjectiveData] of [...selectedObjectives]) {
                if (
                    selectedObjectiveData.selectedApplicationIds.length === 0 &&
                    selectedObjectiveData.selectedLearningResourceIds.length === 0 &&
                    (selectedObjectiveData.applicationNotes?.length ?? 0) === 0 &&
                    (selectedObjectiveData.learningResourceNotes?.length ?? 0) === 0
                ) {
                    this.erroredElements.set(competencyId, [
                        ...(this.erroredElements.get(competencyId) ?? []),
                        objectiveId,
                    ]);
                }
            }
        }

        if (this.erroredElements.size > 0) {
            ToastProvider.error(
                t({
                    id: "competencyDevelopment.assessments.global.errors.noSelectedPracticalApplication",
                    message: "You need to select at least 1 practical application for each objective",
                }),
            );
            return;
        }

        try {
            this.setIsLoading(true);

            await selectObjectives(this.selectedElements);

            this.setIsLoading(false);

            if (fromExternalApp) {
                window.close();

                window.setTimeout(
                    () =>
                        ToastProvider.success(
                            t({
                                id: "competencyDevelopment.assessments.alerts.finalize.toastSuccessMessage",
                                message: "Your changes have been saved. You may now close this window.",
                            }),
                        ),
                    250,
                );
            }
        } catch (e) {
            this.setIsLoading(false);
        }
    };
}
